import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowElbowDownLeft, ClockCounterClockwise, MagnifyingGlass, WarningCircle, X } from '@phosphor-icons/react';
import { useIslandActions } from '../IslandProvider';
import { searchCommands, COMMANDS, type Command } from '../commands';
import { pushRecent, readRecents, type RecentEntry } from '../../../lib/island/recents';
import { useImmoSearch, type SearchHit } from '../../../lib/island/globalSearch';
import { highlightSegments } from '../../../lib/island/fuzzy';
import { istSichereHttpUrl } from '../../../lib/url';
import { baueSuchListe, einblendVerzoegerung, naechsteAuswahl, type SuchAbschnitt, type SuchEintrag } from '../../../lib/island/sucheListe';
import { FARBE, getoent } from '../pille-stil';
import { figgoSignal } from '../figgoSignal';
import { BEFEHL_DARSTELLUNG, STANDARD_DARSTELLUNG, TREFFER_DARSTELLUNG, TYP_LABEL, type Darstellung } from './sucheDarstellung';

type Eintrag = SuchEintrag<Command, RecentEntry, SearchHit>;

const SEITEN = COMMANDS.filter(c => c.id.startsWith('nav-'));
const AKTIONEN = COMMANDS.filter(c => c.id.startsWith('act-') && c.id !== 'act-abmelden');
const AUSWAHL_RAHMEN = `inset 0 0 0 2px ${FARBE.gold}`;
const LINIE = 'rgba(84,84,88,0.6)';
const KOPF: CSSProperties = { fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(235,235,245,0.45)', padding: '0 4px' };

function schluessel(e: Eintrag): string {
  if (e.art === 'befehl') return `${e.abschnitt}-${e.befehl.id}`;
  if (e.art === 'zuletzt') return `zuletzt-${e.zuletzt.id}`;
  return `${e.abschnitt}-${e.treffer.id}`;
}

function darstellungVon(e: Eintrag): Darstellung {
  if (e.art === 'befehl') return BEFEHL_DARSTELLUNG[e.befehl.id] ?? STANDARD_DARSTELLUNG;
  if (e.art === 'zuletzt') return BEFEHL_DARSTELLUNG[e.zuletzt.id] ?? TREFFER_DARSTELLUNG[e.zuletzt.type] ?? STANDARD_DARSTELLUNG;
  return TREFFER_DARSTELLUNG[e.treffer.type] ?? STANDARD_DARSTELLUNG;
}

function Hervorgehoben({ query, text, farbe }: { query: string; text: string; farbe: string }) {
  return (
    <>
      {highlightSegments(query, text).map((s, i) => (s.match ? <span key={i} style={{ color: farbe }}>{s.text}</span> : <span key={i}>{s.text}</span>))}
    </>
  );
}

function IconKachel({ d, groesse, radius, icon }: { d: Darstellung; groesse: number; radius: number; icon: number }) {
  const I = d.icon;
  const gold = d.farbe === FARBE.gold;
  return (
    <span className="flex shrink-0 items-center justify-center" aria-hidden
      style={{ width: groesse, height: groesse, borderRadius: radius, color: gold ? FARBE.goldText : '#fff', background: gold ? FARBE.goldFlaeche : `linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0) 55%), ${d.farbe}`, boxShadow: `0 8px 18px -8px ${getoent(d.farbe, 0.8)}` }}>
      <I size={icon} weight="fill" />
    </span>
  );
}

/** ⌘K-Suche — Seite „search“ im Deck (Handoff Pille §5, Screens 11/22). */
export function SearchPage({ active }: { active: boolean }) {
  const { collapse, onSearchNavigate } = useIslandActions();
  const navigate = useNavigate();
  const ruhig = useReducedMotion() ?? false;

  const [query, setQuery] = useState('');
  const [auswahl, setAuswahl] = useState(0);
  const [recents, setRecents] = useState<RecentEntry[]>([]);
  const [gedrueckt, setGedrueckt] = useState<string | null>(null);
  const [fokus, setFokus] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const druckTimer = useRef<number | null>(null);
  /** nur Tastatur-Navigation scrollt die Auswahl ins Bild (nicht Hover/Öffnen) */
  const perTastatur = useRef(false);

  useEffect(() => {
    if (active) {
      setRecents(readRecents());
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [active]);

  useEffect(() => () => { if (druckTimer.current !== null) window.clearTimeout(druckTimer.current); }, []);

  useEffect(() => { setAuswahl(0); }, [query]);

  const { hits, loading, fehler } = useImmoSearch(query);
  const leer = query.trim().length === 0;

  const eintraege = useMemo<Eintrag[]>(() => baueSuchListe({
    query, zuletzt: recents, seiten: SEITEN, aktionen: AKTIONEN, befehle: leer ? [] : searchCommands(query), treffer: hits,
  }), [query, recents, hits, leer]);

  // Figgo: 650 ms nach der Eingabe – keine Treffer → Schulterzucken, erste Eingabe → liest mit
  const figgoVorher = useRef('');
  useEffect(() => {
    if (leer || loading || fehler) { if (leer) figgoVorher.current = ''; return; }
    const t = window.setTimeout(() => {
      if (eintraege.length === 0) figgoSignal('nohits', { force: true, p: 0.95 });
      else if (figgoVorher.current === '') figgoSignal('typing', { p: 0.4, cool: 6000 });
      figgoVorher.current = query;
    }, 650);
    return () => window.clearTimeout(t);
  }, [query, loading, leer, fehler, eintraege.length]);

  useEffect(() => {
    if (!perTastatur.current) return;
    perTastatur.current = false;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${auswahl}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [auswahl]);

  function oeffneZiel(z: { id: string; type: string; href?: string; externalUrl?: string }) {
    if (z.externalUrl && istSichereHttpUrl(z.externalUrl)) {
      window.open(z.externalUrl, '_blank', 'noopener,noreferrer');
      collapse();
      return;
    }
    if (onSearchNavigate) onSearchNavigate(z.href ?? '', z.id, z.type);
    else if (z.href) navigate(z.href);
    collapse();
  }

  function ausfuehren(e: Eintrag) {
    if (e.art === 'befehl') {
      e.befehl.run({ navigate, onSearchNavigate, collapse });
      return;
    }
    if (e.art === 'zuletzt') {
      pushRecent(e.zuletzt);
      oeffneZiel(e.zuletzt);
      return;
    }
    const h = e.treffer;
    pushRecent({ id: h.id, kind: h.kind, type: h.type, label: h.label, sub: h.sub, href: h.href, externalUrl: h.externalUrl });
    oeffneZiel(h);
  }

  /** Klick: kurzes Drück-Feedback, Ausführen nach 0,18 s (bei reduzierter Bewegung sofort). */
  function klick(e: Eintrag) {
    if (ruhig) { ausfuehren(e); return; }
    if (druckTimer.current !== null) return;
    setGedrueckt(schluessel(e));
    druckTimer.current = window.setTimeout(() => { druckTimer.current = null; ausfuehren(e); }, 180);
  }

  useEffect(() => {
    if (!active) return;
    function onKeyDown(ev: KeyboardEvent) {
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        ev.preventDefault();
        perTastatur.current = true;
        setAuswahl(i => naechsteAuswahl(i, ev.key as 'ArrowUp' | 'ArrowDown', eintraege.length));
      } else if (ev.key === 'Enter') {
        const e = eintraege[auswahl];
        if (e) { ev.preventDefault(); ausfuehren(e); }
      } else if (ev.key === 'Escape') {
        ev.preventDefault();
        collapse();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, eintraege, auswahl]);

  /** gemeinsame Hülle je Eintrag: Einblenden gestaffelt, Drück-Feedback, Hover = Auswahl */
  function huelle(e: Eintrag, idx: number, stil: CSSProperties, children: ReactNode, top = false) {
    const k = schluessel(e);
    return (
      <motion.button
        key={k}
        type="button"
        data-idx={idx}
        aria-selected={idx === auswahl}
        role="option"
        onMouseEnter={() => setAuswahl(idx)}
        onClick={() => klick(e)}
        initial={ruhig ? false : { opacity: 0, y: 12, scale: 0.96 }}
        animate={gedrueckt === k ? { opacity: 1, y: 0, scale: 0.94 } : { opacity: 1, y: 0, scale: 1 }}
        transition={gedrueckt === k
          ? { duration: 0.28 }
          : { duration: top ? 0.55 : 0.46, delay: ruhig ? 0 : einblendVerzoegerung(idx), ease: [0.2, 0.8, 0.2, 1] }}
        className="relative cursor-pointer text-left"
        style={{ border: 'none', fontFamily: 'inherit', color: '#fff', ...stil }}
      >
        {children}
      </motion.button>
    );
  }

  /* ---------------------------------------------------------- Abschnitte */

  const nachAbschnitt = (a: SuchAbschnitt) => eintraege.map((e, idx) => ({ e, idx })).filter(x => x.e.abschnitt === a);

  function zeilen(liste: { e: Eintrag; idx: number }[]) {
    return (
      <div className="flex flex-col overflow-hidden" style={{ borderRadius: 20, background: 'rgba(255,255,255,0.06)' }}>
        {liste.map(({ e, idx }, i) => {
          const d = darstellungVon(e);
          const aktiv = idx === auswahl;
          const titel = e.art === 'befehl' ? e.befehl.label : e.art === 'treffer' ? e.treffer.label : e.zuletzt.label;
          const unter = e.art === 'treffer' ? e.treffer.sub : e.art === 'befehl' ? e.befehl.hint : undefined;
          const typ = e.art === 'treffer' ? TYP_LABEL[e.treffer.type] ?? e.treffer.type : undefined;
          return huelle(e, idx, { height: 56, padding: '0 14px 0 12px', background: aktiv ? 'rgba(255,255,255,0.13)' : 'transparent', borderRadius: 0 }, (<>
              <span className="flex h-full items-center" style={{ gap: 12 }}>
                <IconKachel d={d} groesse={34} radius={10} icon={18} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate" style={{ fontSize: 14.5, fontWeight: 700 }}><Hervorgehoben query={query} text={titel} farbe={FARBE.gold} /></span>
                  {unter && <span className="block truncate" style={{ fontSize: 12, color: FARBE.text2 }}>{unter}</span>}
                </span>
                {typ && <span className="shrink-0" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(235,235,245,0.45)' }}>{typ}</span>}
                {aktiv && <ArrowElbowDownLeft size={15} weight="bold" color={FARBE.gold} aria-hidden />}
              </span>
              {i < liste.length - 1 && <span aria-hidden className="absolute bottom-0 right-0" style={{ left: 58, height: 0.5, background: LINIE }} />}
            </>));
        })}
      </div>
    );
  }

  function leerAnsicht() {
    const zuletzt = nachAbschnitt('Zuletzt');
    const seiten = nachAbschnitt('Seiten');
    const aktionen = nachAbschnitt('Aktionen');
    return (
      <>
        {zuletzt.length > 0 && (
          <section className="flex flex-col" style={{ gap: 8 }}>
            <h3 style={KOPF}>Zuletzt</h3>
            <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
              {zuletzt.map(({ e, idx }) => {
                if (e.art !== 'zuletzt') return null;
                const r = e.zuletzt;
                const istBefehl = COMMANDS.some(c => c.id === r.id);
                const d = darstellungVon(e);
                const I = d.icon;
                return huelle(e, idx, { height: 76, padding: '0 14px', borderRadius: 20, background: 'rgba(255,255,255,0.07)', boxShadow: idx === auswahl ? AUSWAHL_RAHMEN : 'inset 0 0 0 0.5px rgba(255,255,255,0.08)' }, (<>
                    <span className="flex h-full items-center" style={{ gap: 12 }}>
                      <span className="flex shrink-0 items-center justify-center rounded-full" aria-hidden style={{ width: 44, height: 44, background: getoent(d.farbe, 0.22), color: d.farbe }}>
                        <I size={22} weight="fill" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate" style={{ fontSize: 15, fontWeight: 700 }}>{r.label}</span>
                        <span className="flex items-center truncate" style={{ gap: 4, fontSize: 12, color: FARBE.text2 }}>
                          <ClockCounterClockwise size={12} aria-hidden /> {istBefehl ? 'Befehl' : TYP_LABEL[r.type] ?? r.type}
                        </span>
                      </span>
                    </span>
                  </>));
              })}
            </div>
          </section>
        )}
        <section className="flex flex-col" style={{ gap: 8 }}>
          <h3 style={KOPF}>Seiten</h3>
          <div className="grid" style={{ gap: 6, padding: 12, borderRadius: 22, background: 'rgba(255,255,255,0.06)', gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))' }}>
            {seiten.map(({ e, idx }) => (
              e.art === 'befehl' && huelle(e, idx, { padding: '8px 2px', borderRadius: 16, background: 'transparent', boxShadow: idx === auswahl ? AUSWAHL_RAHMEN : 'none' }, (<>
                  <span className="flex flex-col items-center" style={{ gap: 6 }}>
                    <IconKachel d={darstellungVon(e)} groesse={54} radius={15} icon={26} />
                    <span className="max-w-full truncate" style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.86)' }}>{e.befehl.label}</span>
                  </span>
                </>))
            ))}
          </div>
        </section>
        <section className="flex flex-col" style={{ gap: 8 }}>
          <h3 style={KOPF}>Aktionen</h3>
          <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
            {aktionen.map(({ e, idx }) => (
              e.art === 'befehl' && huelle(e, idx, { height: 54, padding: '0 12px', borderRadius: 16, background: 'rgba(255,255,255,0.07)', boxShadow: idx === auswahl ? AUSWAHL_RAHMEN : 'none' }, (<>
                  <span className="flex h-full items-center" style={{ gap: 10 }}>
                    <IconKachel d={darstellungVon(e)} groesse={32} radius={10} icon={17} />
                    <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 700 }}>{e.befehl.label}</span>
                  </span>
                </>))
            ))}
          </div>
        </section>
      </>
    );
  }

  function trefferAnsicht() {
    const top = nachAbschnitt('Top-Treffer')[0];
    const anzahl = hits.length;
    const gruppen: SuchAbschnitt[] = ['Kontakte', 'Treffer', 'E-Mails', 'Dokumente', 'Befehle'];
    const fehlerHinweis = fehler && (
      <div role="alert" className="flex items-center" style={{ gap: 10, padding: '10px 14px', borderRadius: 16, background: 'rgba(255,69,58,0.14)', color: '#ff8a80', fontSize: 13, fontWeight: 700 }}>
        <WarningCircle size={18} weight="fill" aria-hidden /> Suche fehlgeschlagen – bitte erneut versuchen.
      </div>
    );
    if (eintraege.length === 0) {
      if (loading) return null;
      if (fehler) return fehlerHinweis;
      return (
        <div className="flex flex-1 flex-col items-center justify-center text-center" style={{ gap: 6, padding: '28px 0' }}>
          <span className={ruhig ? 'flex items-center justify-center rounded-full' : 'pille-schweben flex items-center justify-center rounded-full'}
            style={{ width: 64, height: 64, background: 'rgba(255,255,255,0.08)', color: 'rgba(235,235,245,0.45)', marginBottom: 6 }} aria-hidden>
            <MagnifyingGlass size={28} />
          </span>
          <span style={{ fontSize: 17, fontWeight: 800 }}>Keine Treffer</span>
          <span style={{ fontSize: 13, color: FARBE.text2 }}>für „{query.trim()}“</span>
        </div>
      );
    }
    return (
      <>
        {fehlerHinweis}
        {top && top.e.art === 'treffer' && (() => {
          const h = top.e.treffer;
          const d = darstellungVon(top.e);
          const BG = d.icon;
          return (
            <section className="flex flex-col" style={{ gap: 8 }}>
              <h3 style={KOPF}>Top-Treffer</h3>
              {huelle(top.e, top.idx, { padding: 16, borderRadius: 24, overflow: 'hidden', background: `linear-gradient(150deg, ${getoent(d.farbe, 0.26)}, ${getoent(d.farbe, 0.08)} 65%, rgba(255,255,255,0.05))`, boxShadow: top.idx === auswahl ? AUSWAHL_RAHMEN : `inset 0 0 0 0.5px ${getoent(d.farbe, 0.4)}` }, (<>
                <BG aria-hidden size={110} className="pointer-events-none absolute" style={{ right: -14, bottom: -24, opacity: 0.07 }} />
                <span className="relative flex items-center" style={{ gap: 14 }}>
                  <IconKachel d={d} groesse={60} radius={18} icon={30} />
                  <span className="min-w-0 flex-1">
                    <span className="inline-flex items-center" style={{ height: 20, padding: '0 8px', borderRadius: 10, fontSize: 11, fontWeight: 800, background: getoent(d.farbe, 0.22), color: '#fff' }}>
                      {TYP_LABEL[h.type] ?? h.type}
                    </span>
                    <span className="block truncate" style={{ marginTop: 4, fontSize: 18, fontWeight: 800 }}><Hervorgehoben query={query} text={h.label} farbe="#f2d9a8" /></span>
                    {h.sub && <span className="block truncate" style={{ fontSize: 13, color: FARBE.text2 }}>{h.sub}</span>}
                  </span>
                  <span className="hidden shrink-0 items-center sm:flex" style={{ height: 34, gap: 6, padding: '0 14px', borderRadius: 17, fontSize: 13, fontWeight: 800, background: 'rgba(255,255,255,0.14)' }}>
                    Öffnen <ArrowElbowDownLeft size={13} weight="bold" aria-hidden />
                  </span>
                </span>
              </>), true)}
            </section>
          );
        })()}
        {anzahl > 0 && <span style={{ ...KOPF, textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>{anzahl === 1 ? '1 Treffer' : `${anzahl} Treffer`}</span>}
        {gruppen.map(g => {
          const liste = nachAbschnitt(g);
          if (liste.length === 0) return null;
          return (
            <section key={g} className="flex flex-col" style={{ gap: 8 }}>
              <h3 style={KOPF}>{g}</h3>
              {zeilen(liste)}
            </section>
          );
        })}
      </>
    );
  }

  return (
    <div className="flex h-full flex-col text-white">
      <div style={{ padding: '2px 16px 10px' }}>
        <div data-bj="search" className="flex items-center"
          style={{
            height: 52, borderRadius: 18, padding: '0 8px 0 16px', gap: 10, background: 'rgba(118,118,128,0.24)',
            boxShadow: fokus && !leer ? 'inset 0 0 0 1.5px rgba(242,217,168,0.55), 0 0 0 4px rgba(242,217,168,0.12)' : 'none',
            transition: 'box-shadow .25s',
          }}>
          <MagnifyingGlass size={20} weight="bold" color={leer ? FARBE.text2 : FARBE.gold} aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setFokus(true)}
            onBlur={() => setFokus(false)}
            placeholder="Objekte, Kontakte, Tickets, Befehle …"
            aria-label="Suchen"
            role="combobox"
            aria-expanded
            aria-controls="pille-suchliste"
            className="min-w-0 flex-1 bg-transparent text-white placeholder:text-[rgba(235,235,245,0.45)] focus:outline-none"
            style={{ fontSize: 17, fontWeight: 600, border: 'none', outline: 'none', boxShadow: 'none', fontFamily: 'inherit' }}
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <span aria-hidden className="pille-dreh shrink-0 rounded-full" style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.2)', borderTopColor: FARBE.gold }} />}
          {!leer && (
            <button type="button" onClick={() => { setQuery(''); inputRef.current?.focus(); }} aria-label="Eingabe leeren"
              className="flex shrink-0 cursor-pointer items-center justify-center rounded-full"
              style={{ width: 28, height: 28, border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff' }}>
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
      </div>

      <div ref={listRef} id="pille-suchliste" role="listbox" aria-label="Suchergebnisse"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto" style={{ gap: 16, padding: '4px 16px 14px' }}>
        {leer ? leerAnsicht() : trefferAnsicht()}
      </div>

      <div className="flex shrink-0 items-center justify-center" aria-hidden
        style={{ height: 38, gap: 18, borderTop: '0.5px solid rgba(255,255,255,0.1)', fontSize: 11, fontWeight: 600, color: 'rgba(235,235,245,0.45)' }}>
        <span>↑↓ navigieren</span>
        <span>↵ öffnen</span>
        <span>esc schließen</span>
      </div>
    </div>
  );
}
